<template>
  <div id="practice-view-evaluation" :class="{readonly: !editable}">
    <div class="global-mean" v-if="showMean && globalMean !== ''">
      <div :class="'evaluation-' + level(globalMean)">
        {{ globalMean }}
      </div>
    </div>
    <div class="practice-view-evaluation-group" v-for="(group, i) in evaluation.groups" v-bind:key="group.id" v-show="!groupEmpty(group)">
      <div class="title">
        <div>
          {{ group.name }}
        </div>
        <div class="badge">
          <div :class="'evaluation-' + level(evaluationMean(group))">
            {{ evaluationMean(group) }}
          </div>
        </div>
      </div>
      <div class="practice-view-evaluation-group-content">
        <div class="practice-view-evaluation-group-item" v-for="(item, j) in group.items" v-bind:key="item.id" v-show="withoutPracticed === false || (withoutPracticed === true && item.value === 1)">
          <div>
            {{ item.name }}
          </div>
          <div>
            <div :class="{marked: item.value === 1}" v-on:click="studentEvaluationUpdate(i, j, 1)">1</div>
            <div :class="{marked: item.value === 2}" v-on:click="studentEvaluationUpdate(i, j, 2)">2</div>
            <div :class="{marked: item.value === 3}" v-on:click="studentEvaluationUpdate(i, j, 3)">3</div>
            <div :class="{marked: item.value === 4}" v-on:click="studentEvaluationUpdate(i, j, 4)">4</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'student-evaluation-practice-component',
  data () {
    return {
      show: false,
      recompile: false
    }
  },
  watch: {
    globalMean: {
      immediate: true,
      handler (value) {
        this.$emit('onMean', value, value === '' ? '' : this.level(value))
      }
    }
  },
  computed: {
    globalMean () {
      let mean = 0
      let length = 0

      if (this.recompile === true) {
        mean = 0
        length = 0
      }

      for (let i in this.evaluation.groups) {
        let group = this.evaluation.groups[i]
        if (group.items.length > 0) {
          mean += this.evaluationMean(group)
          length++
        }
      }

      // A business without evaluation groups has no mean, and it showed NaN.
      if (length === 0) {
        return ''
      }

      return Math.round(mean / length)
    }
  },
  props: {
    'app': {
      type: Object,
      default: () => {
        return {}
      }
    },
    'student': {
      type: String,
      default: () => {
        return ''
      }
    },
    // Off when the screen shows the mean in its own layout (onMean).
    'showMean': {
      type: Boolean,
      default: () => {
        return true
      }
    },
    'evaluation': {
      type: Object,
      default: () => {
        return {
          groups: [],
          items: []
        }
      }
    },
    'withoutPracticed': {
      type: Boolean,
      default: () => {
        return false
      }
    },
    'editable': {
      type: Boolean,
      default: () => {
        return true
      }
    }
  },
  methods: {
    studentEvaluationUpdate (i, j, value) {
      if (this.editable === false) {
        return
      }

      this.recompile = true
      setTimeout(() => {
        this.recompile = false
        this.$forceUpdate()
      }, 100)

      this.$set(this.evaluation.groups[i].items[j], 'value', value)
      this.rc++
      this.$emit('onChange')

      this.$forceUpdate()

      if (this.withoutPracticed === true && this.withoutPracticedCount === 0) {
        this.$emit('onPractice')
      }

      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.evaluation.update,
        {
          business_id: this.evaluation.groups[i].items[j].business_id,
          student: this.student,
          item: this.evaluation.groups[i].items[j].id,
          value: value
        },
        {
          emulateJSON: true,
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        if (response.status === 200) {
          if (response.body.success === false) {
            if (response.body.error.code === 'S100') {
              this.app.methods.logout()
            } else {
              this.showRequestError(this.app.strings, 'request', response.body.error)
            }
          }
        } else {
          this.showError(this.app.strings, 'request', 'status-' + response.status)
        }
      }, () => {
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    groupEmpty (group) {
      if (this.withoutPracticed === true) {
        for (let i in group.items) {
          let item = group.items[i]
          if (item.value === 1) {
            return false
          }
        }
        return true
      } else {
        return group.items.length === 0
      }
    },
    // Exact percentage: the scale goes from 1 (0 %) to 4 (100 %).
    evaluationMean (group) {
      let total = 0
      for (let i in group.items) {
        total += group.items[i].value
      }

      return Math.round((total / group.items.length - 1) * 100 / 3)
    },
    // Colour band of a percentage.
    level (percent) {
      if (percent === 100) {
        return 100
      }

      return Math.floor(percent / 25) * 25
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
// Valkiria look (only used in the app screens: class in progress and the
// student's evolution).
$va-primary: #2071b7;
$va-success: #15803d;
$va-teal: #0f766e;
$va-muted: #64748b;
$va-border: #e8edf3;

#practice-view-evaluation {
  flex: 1;
  overflow: auto;
  padding-top: 12px;
  box-sizing: border-box;
}

.global-mean {
  display: flex;
  align-items: center;
  justify-content: center;
}

.global-mean > div {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 999px;
  border: 8px solid #dbe8f5;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.08);
  color: $va-muted;
  font-family: 'Lexend', sans-serif;
  font-size: 26px;
  font-weight: 700;
}

.global-mean > div::after {
  content: '%';
  font-size: 14px;
  margin-left: 2px;
}

.global-mean > div.evaluation-50 { border-color: rgba(32, 113, 183, 0.45); color: $va-primary; }
.global-mean > div.evaluation-75 { border-color: rgba(15, 118, 110, 0.45); color: $va-teal; }
.global-mean > div.evaluation-100 { border-color: rgba(22, 163, 74, 0.55); color: $va-success; }

.practice-view-evaluation-group {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid $va-border;
  border-radius: 20px;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.08);
}

.practice-view-evaluation-group > .title {
  display: flex;
  align-items: center;
  padding-bottom: 8px;
}

.practice-view-evaluation-group > .title > div {
  flex: 1;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.practice-view-evaluation-group > .title > .badge {
  display: flex;
  flex: 0;
  align-items: center;
  justify-content: flex-end;
}

.practice-view-evaluation-group > .title > .badge > div {
  padding: 4px 10px;
  border-radius: 999px;
  background-color: rgba(100, 116, 139, 0.12);
  color: $va-muted;
  font-family: 'Lexend', sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.practice-view-evaluation-group > .title > .badge > div::after {
  content: '%';
}

.practice-view-evaluation-group > .title > .badge > div.evaluation-50 { background-color: rgba(32, 113, 183, 0.1); color: $va-primary; }
.practice-view-evaluation-group > .title > .badge > div.evaluation-75 { background-color: rgba(15, 118, 110, 0.12); color: $va-teal; }
.practice-view-evaluation-group > .title > .badge > div.evaluation-100 { background-color: rgba(22, 163, 74, 0.12); color: $va-success; }

.practice-view-evaluation-group-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid $va-border;
}

.practice-view-evaluation-group-item > div:nth-of-type(1) {
  flex: 1;
  color: #1f2a37;
  font-size: 14px;
}

.practice-view-evaluation-group-item > div:nth-of-type(2) {
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 12px;
  background-color: #f5f8fb;
}

.practice-view-evaluation-group-item > div:nth-of-type(1) {
  min-width: 0;
  padding-right: 10px;
  overflow-wrap: break-word;
}

.practice-view-evaluation-group-item > div:nth-of-type(2) > div {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background-color: #FFFFFF;
  border: 1px solid $va-border;
  color: $va-muted;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.practice-view-evaluation-group-item > div:nth-of-type(2) > div.marked {
  color: #FFFFFF;
}

.practice-view-evaluation-group-item > div:nth-of-type(2) > div:nth-of-type(1).marked { background-color: #64748b; border-color: #64748b; }
.practice-view-evaluation-group-item > div:nth-of-type(2) > div:nth-of-type(2).marked { background-color: $va-primary; border-color: $va-primary; }
.practice-view-evaluation-group-item > div:nth-of-type(2) > div:nth-of-type(3).marked { background-color: $va-teal; border-color: $va-teal; }
.practice-view-evaluation-group-item > div:nth-of-type(2) > div:nth-of-type(4).marked { background-color: $va-success; border-color: $va-success; }

// Read only: the marks are data, not buttons.
.readonly .practice-view-evaluation-group-item > div:nth-of-type(2) {
  background-color: transparent;
  pointer-events: none;
}

.readonly .practice-view-evaluation-group-item > div:nth-of-type(2) > div:not(.marked) {
  border-color: transparent;
  background-color: #f5f8fb;
}

// The narrowest phones have no room for a name beside the four marks.
@media (max-width: 359px) {
  .practice-view-evaluation-group-item {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .practice-view-evaluation-group-item > div:nth-of-type(1) {
    padding-right: 0;
  }

  .practice-view-evaluation-group-item > div:nth-of-type(2) > div {
    flex: 1;
    width: auto;
  }
}

@media (min-width: 1024px) {
  // Skill groups in columns on desktop.
  #practice-view-evaluation {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 16px;
    align-content: start;
  }

  #practice-view-evaluation > .global-mean {
    grid-column: 1 / -1;
  }

  .practice-view-evaluation-group {
    margin-top: 0;
  }
}
</style>
